import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, Eye, EyeOff, Loader2 } from "lucide-react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { getFriendlyAuthErrorMessage } from "@/lib/authErrorMessages";

interface LoginProps {
  initialMode?: "login" | "register";
}

const GoogleIcon = () => (
  <svg aria-hidden="true" className="h-4 w-4" viewBox="0 0 24 24">
    <path
      d="M21.805 10.023H12.24v3.955h5.482c-.236 1.272-.945 2.35-2.009 3.073v2.553h3.25c1.902-1.75 2.996-4.33 2.996-7.389 0-.73-.066-1.432-.154-2.192Z"
      fill="#4285F4"
    />
    <path
      d="M12.24 22c2.718 0 4.997-.901 6.663-2.423l-3.25-2.553c-.901.605-2.054.967-3.413.967-2.622 0-4.843-1.771-5.636-4.154H3.245v2.633A10.056 10.056 0 0 0 12.24 22Z"
      fill="#34A853"
    />
    <path
      d="M6.604 13.837a6.04 6.04 0 0 1-.315-1.837c0-.638.11-1.257.315-1.837V7.53H3.245A10.056 10.056 0 0 0 2.16 12c0 1.61.385 3.135 1.085 4.47l3.359-2.633Z"
      fill="#FBBC04"
    />
    <path
      d="M12.24 5.99c1.478 0 2.806.509 3.85 1.507l2.887-2.887C17.232 2.979 14.953 2 12.24 2a10.056 10.056 0 0 0-8.995 5.53l3.359 2.633C7.397 7.761 9.618 5.99 12.24 5.99Z"
      fill="#EA4335"
    />
  </svg>
);

const Login = ({ initialMode = "login" }: LoginProps) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showSignUpConfirmPassword, setShowSignUpConfirmPassword] = useState(false);
  const [registrationDone, setRegistrationDone] = useState(false);

  const isRegisterRoute = location.pathname === "/register";
  const [isSignUp, setIsSignUp] = useState(initialMode === "register" || isRegisterRoute);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isRegisterRoute || initialMode === "register") {
      setIsSignUp(true);
    }
  }, [isRegisterRoute, initialMode]);

  useEffect(() => {
    let cancelled = false;

    const detectRecoveryFlow = async () => {
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const hashType = hashParams.get("type");
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");

      const queryType = searchParams.get("type");
      const tokenHash = searchParams.get("token_hash");

      const shouldEnterRecoveryMode = hashType === "recovery" || queryType === "recovery";
      if (!shouldEnterRecoveryMode || cancelled) return;

      setIsSignUp(false);

      try {
        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;

          // Clean auth tokens from URL hash after establishing session.
          window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
          if (!cancelled) setIsRecoveryMode(true);
          return;
        }

        if (queryType === "recovery" && tokenHash) {
          const { error } = await (supabase.auth as any).verifyOtp({
            type: "recovery",
            token_hash: tokenHash,
          });
          if (error) throw error;
          if (!cancelled) setIsRecoveryMode(true);
          return;
        }

        const { data: { session } } = await supabase.auth.getSession();
        if (!cancelled && session) {
          setIsRecoveryMode(true);
        }
      } catch (err: any) {
        toast.error(getFriendlyAuthErrorMessage(err, "Your reset link is invalid or expired. Request a new one."));
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsSignUp(false);
        setIsRecoveryMode(true);
      }
    });

    detectRecoveryFlow();

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [searchParams]);

  const handleForgotPassword = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Please enter your email address first.");
      return;
    }
    setIsLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
      redirectTo: `${window.location.origin}/login`,
    });
    if (error) {
      toast.error(getFriendlyAuthErrorMessage(error, "Failed to send reset email."));
    } else {
      setResetSent(true);
    }
    setIsLoading(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const { error } = await signIn(email, password);
    if (error) {
      if (/too many failed login attempts/i.test(error.message)) {
        toast.error(error.message);
      } else {
        toast.error("Incorrect email or password. Please try again.");
      }
    } else {
      // Check for pending invite token (set by InvitePage when email confirmation is required)
      const raw = localStorage.getItem("pending_invite");
      if (raw) {
        try {
          const tokenData = JSON.parse(raw);
          if (tokenData?.token) {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              await supabase.rpc("accept_invitation" as any, { _token: tokenData.token, _user_id: user.id });
            }
          }
        } catch (err: any) {
          toast.error(getFriendlyAuthErrorMessage(err, "Could not finish accepting your invitation."));
        }
        localStorage.removeItem("pending_invite");
      }
      navigate("/dashboard");
    }
    setIsLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          // After email confirmation, send new users straight to church setup
          emailRedirectTo: `${window.location.origin}/setup`,
        },
      });
      if (error) throw error;
      if (data.user) {
        if (!data.user.identities || data.user.identities.length === 0) {
          throw new Error("This email is already registered. Please sign in instead.");
        }
        setRegistrationDone(true);
      }
    } catch (err: any) {
      toast.error(getFriendlyAuthErrorMessage(err, "Failed to create account. Please try again."));
    }
    setIsLoading(false);
  };

  const handleGoogleAuth = async () => {
    setIsGoogleLoading(true);

    const redirectPath = isSignUp ? "/setup" : "/dashboard";
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}${redirectPath}`,
      },
    });

    if (error) {
      setIsGoogleLoading(false);
      toast.error(getFriendlyAuthErrorMessage(error, "Google sign-in failed. Please try again."));
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsResettingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      toast.error(getFriendlyAuthErrorMessage(error, "Failed to update password."));
      setIsResettingPassword(false);
      return;
    }

    
    setIsRecoveryMode(false);
    setNewPassword("");
    setConfirmNewPassword("");
    await supabase.auth.signOut();
    setIsResettingPassword(false);
    navigate("/login", { replace: true });
  };

  // ── Registration confirmation screen ────────────────────────────────────────
  if (registrationDone) {
    return (
      <div className="relative min-h-screen overflow-hidden brand-page-bg flex items-center justify-center p-4">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-0 h-72 w-72 rounded-full bg-[#dbe9ff] blur-3xl" />
          <div className="absolute right-0 top-20 h-72 w-72 rounded-full bg-[#f8efe2] blur-3xl" />
        </div>
        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <img src="/landing_icd_logo.png" alt="iChurch Data" className="w-14 h-14 rounded-2xl object-cover mx-auto mb-4 shadow-md shadow-[#1e3a8a]/20" />
            <h1 className="text-2xl font-heading font-bold text-slate-900">Check Your Email</h1>
          </div>
          <div className="rounded-2xl border border-border bg-card p-8 shadow-[0_16px_40px_-24px_rgba(2,6,23,0.35)] text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Confirm your account</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              We sent a confirmation link to <span className="font-semibold text-slate-800">{email}</span>.<br />
              Please check your inbox and click the link to activate your account.
            </p>
            <p className="text-xs text-slate-500">Don't see it? Check your spam or junk folder.</p>
            <button
              type="button"
              onClick={() => { setRegistrationDone(false); setIsSignUp(false); }}
              className="text-sm text-[#1e3a8a] font-medium hover:underline"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Password reset confirmation screen
  if (resetSent) {
    return (
      <div className="relative min-h-screen overflow-hidden brand-page-bg flex items-center justify-center p-4">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-0 h-72 w-72 rounded-full bg-[#dbe9ff] blur-3xl" />
          <div className="absolute right-0 top-20 h-72 w-72 rounded-full bg-[#f8efe2] blur-3xl" />
        </div>
        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <img src="/landing_icd_logo.png" alt="iChurch Data" className="w-14 h-14 rounded-2xl object-cover mx-auto mb-4 shadow-md shadow-[#1e3a8a]/20" />
            <h1 className="text-2xl font-heading font-bold text-slate-900">Check Your Email</h1>
          </div>
          <div className="rounded-2xl border border-border bg-card p-8 shadow-[0_16px_40px_-24px_rgba(2,6,23,0.35)] text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Password Reset Link Sent</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              We sent a password reset link to <span className="font-semibold text-slate-800">{email}</span>.<br />
              Click the link in the email to set a new password.
            </p>
            <p className="text-xs text-slate-500">Don't see it? Check your spam or junk folder.</p>
            <button
              type="button"
              onClick={() => { setResetSent(false); }}
              className="text-sm text-[#1e3a8a] font-medium hover:underline"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Password recovery screen (opened from reset email link)
  if (isRecoveryMode) {
    return (
      <div className="relative min-h-screen overflow-hidden brand-page-bg flex items-center justify-center p-4">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-0 h-72 w-72 rounded-full bg-[#dbe9ff] blur-3xl" />
          <div className="absolute right-0 top-20 h-72 w-72 rounded-full bg-[#f8efe2] blur-3xl" />
        </div>
        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <img src="/landing_icd_logo.png" alt="iChurch Data" className="w-14 h-14 rounded-2xl object-cover mx-auto mb-4 shadow-md shadow-[#1e3a8a]/20" />
            <h1 className="text-2xl font-heading font-bold text-slate-900">Set New Password</h1>
          </div>

          <form onSubmit={handleUpdatePassword} className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-[0_16px_40px_-24px_rgba(2,6,23,0.35)]">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="••••••••"
                onFocus={(e) => e.target.placeholder = ''}
                onBlur={(e) => e.target.placeholder = '••••••••'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="border-input bg-background"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
              <Input
                id="confirmNewPassword"
                type="password"
                placeholder="••••••••"
                onFocus={(e) => e.target.placeholder = ''}
                onBlur={(e) => e.target.placeholder = '••••••••'}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                minLength={6}
                className={`border-input bg-background ${confirmNewPassword && confirmNewPassword !== newPassword ? "border-red-400 focus-visible:ring-red-400" : ""}`}
              />
              {confirmNewPassword && confirmNewPassword !== newPassword && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-[#1e3a8a] hover:bg-[#162d70]"
              disabled={isResettingPassword || (!!confirmNewPassword && confirmNewPassword !== newPassword)}
            >
              {isResettingPassword ? "Updating..." : "Update Password"}
            </Button>
            <p className="text-center text-sm">
              <button
                type="button"
                onClick={() => {
                  setIsRecoveryMode(false);
                  navigate("/login", { replace: true });
                }}
                className="inline-flex items-center gap-1 text-[#1e3a8a] font-medium hover:underline"
              >
                <ChevronLeft className="h-4 w-4" /> Back to Sign In
              </button>
            </p>
          </form>
        </div>
      </div>
    );
  }

  // Sign-in form
  if (!isSignUp) {
    return (
      <div className="relative min-h-screen overflow-hidden brand-page-bg flex items-center justify-center p-4">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-0 h-72 w-72 rounded-full bg-[#dbe9ff] blur-3xl" />
          <div className="absolute right-0 top-20 h-72 w-72 rounded-full bg-[#f8efe2] blur-3xl" />
        </div>
        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <img src="/landing_icd_logo.png" alt="iChurch Data" className="w-14 h-14 rounded-2xl object-cover mx-auto mb-4 shadow-md shadow-[#1e3a8a]/20" />
            <h1 className="text-2xl font-heading font-bold text-slate-900">iChurch Data</h1>
          </div>

          <form onSubmit={handleSignIn} className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-[0_16px_40px_-24px_rgba(2,6,23,0.35)]">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="admin@church.org" onFocus={(e) => e.target.placeholder = ''} onBlur={(e) => e.target.placeholder = 'admin@church.org'} value={email} onChange={(e) => setEmail(e.target.value)} required className="border-input bg-background" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showSignInPassword ? "text" : "password"}
                  placeholder="••••••••"
                  onFocus={(e) => e.target.placeholder = ''}
                  onBlur={(e) => e.target.placeholder = '••••••••'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="border-input bg-background pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowSignInPassword((value) => !value)}
                  className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-500 transition-colors hover:text-slate-700"
                  aria-label={showSignInPassword ? "Hide password" : "Show password"}
                >
                  {showSignInPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full bg-[#1e3a8a] hover:bg-[#162d70]" disabled={isLoading || isGoogleLoading}>
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
            <p className="text-center">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={isLoading}
                className="text-sm text-[#1e3a8a] font-medium hover:underline disabled:opacity-50"
              >
                Forgot password?
              </button>
            </p>
            <div className="flex items-center gap-3 pt-1">
              <Separator className="flex-1" />
              <span className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">or</span>
              <Separator className="flex-1" />
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleGoogleAuth}
              disabled={isLoading || isGoogleLoading}
            >
              {isGoogleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleIcon />}
              <span>{isGoogleLoading ? "Connecting to Google..." : "Continue with Google"}</span>
            </Button>
            <p className="text-center text-sm text-slate-600">
              Don't have an account?{" "}
              <button type="button" onClick={() => setIsSignUp(true)} className="text-[#1e3a8a] font-medium hover:underline">
                Sign up
              </button>
            </p>
            <p className="text-center text-sm">
              <Link to="/" className="inline-flex items-center gap-1 text-[#1e3a8a] font-medium hover:underline">
                <ChevronLeft className="h-4 w-4" /> Back to Home
              </Link>
            </p>
          </form>
        </div>
      </div>
    );
  }

  // Single-step signup
  return (
    <div className="relative min-h-screen overflow-hidden brand-page-bg flex items-center justify-center p-4">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-0 h-72 w-72 rounded-full bg-[#dbe9ff] blur-3xl" />
        <div className="absolute right-0 top-20 h-72 w-72 rounded-full bg-[#f8efe2] blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-[#e8f4ef] blur-3xl" />
      </div>
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <img src="/landing_icd_logo.png" alt="iChurch Data" className="w-14 h-14 rounded-2xl object-cover mx-auto mb-4 shadow-md shadow-[#1e3a8a]/20" />
          <h1 className="text-2xl font-heading font-bold text-slate-900">Create Your Account</h1>
          <p className="text-slate-600 mt-1">Get started with iChurch Data</p>
        </div>

        <form onSubmit={handleSignUp} className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-[0_16px_40px_-24px_rgba(2,6,23,0.35)]">
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@church.org" onFocus={(e) => e.target.placeholder = ''} onBlur={(e) => e.target.placeholder = 'admin@church.org'} required className="border-input bg-background" />
          </div>
          <div className="space-y-2">
            <Label>Password *</Label>
            <div className="relative">
              <Input
                type={showSignUpPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                onFocus={(e) => e.target.placeholder = ''}
                onBlur={(e) => e.target.placeholder = '••••••••'}
                required
                minLength={6}
                className="border-input bg-background pr-10"
              />
              <button
                type="button"
                onClick={() => setShowSignUpPassword((value) => !value)}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-500 transition-colors hover:text-slate-700"
                aria-label={showSignUpPassword ? "Hide password" : "Show password"}
              >
                {showSignUpPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Confirm Password *</Label>
            <div className="relative">
              <Input
                type={showSignUpConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                onFocus={(e) => e.target.placeholder = ''}
                onBlur={(e) => e.target.placeholder = '••••••••'}
                required
                minLength={6}
                className={`border-input bg-background pr-10 ${confirmPassword && confirmPassword !== password ? "border-red-400 focus-visible:ring-red-400" : ""}`}
              />
              <button
                type="button"
                onClick={() => setShowSignUpConfirmPassword((value) => !value)}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-500 transition-colors hover:text-slate-700"
                aria-label={showSignUpConfirmPassword ? "Hide password" : "Show password"}
              >
                {showSignUpConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {confirmPassword && confirmPassword !== password && (
              <p className="text-xs text-red-500">Passwords do not match</p>
            )}
          </div>
          <Button type="submit" disabled={isLoading || isGoogleLoading || (!!confirmPassword && confirmPassword !== password)} className="w-full bg-[#1e3a8a] hover:bg-[#162d70]">
            {isLoading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creating...</> : "Create Account"}
          </Button>
          <div className="flex items-center gap-3 pt-1">
            <Separator className="flex-1" />
            <span className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">or</span>
            <Separator className="flex-1" />
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleAuth}
            disabled={isLoading || isGoogleLoading}
          >
            {isGoogleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleIcon />}
            <span>{isGoogleLoading ? "Connecting to Google..." : "Continue with Google"}</span>
          </Button>
          <p className="text-center text-sm text-slate-600">
            Already have an account?{" "}
            <button type="button" onClick={() => setIsSignUp(false)} className="text-[#1e3a8a] font-medium hover:underline">
              Sign in
            </button>
          </p>
          <p className="text-center text-sm">
            <Link to="/" className="inline-flex items-center gap-1 text-[#1e3a8a] font-medium hover:underline">
              <ChevronLeft className="h-4 w-4" /> Back to Home
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Login;
